import { useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Plus,
  Clock,
  User,
  Trash2,
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  addMonths,
  subMonths,
  isSameMonth,
  isToday,
  parseISO,
  isSameDay,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CalendarDay,
  CalendarMonth,
  CalendarProfessional,
  CalendarServiceType,
  CalendarSlot,
} from '../../types/calendar';
import { Button, Card, FormSelect, Badge, Modal } from '../ui';
import { cn } from '../../lib/utils';

interface CalendarViewProps {
  professionals: CalendarProfessional[];
  serviceTypes: CalendarServiceType[];
  slots: CalendarSlot[];
  isLoading: boolean;
  onDateClick: (date: string) => void;
  onProfessionalChange: (professionalId: number) => void;
  selectedProfessionalId: number | null;
  selectedDate?: string | null;
  renderDayContent: (date: string) => React.ReactNode;
}

function CalendarView({
  professionals,
  serviceTypes,
  slots,
  isLoading,
  onDateClick,
  onProfessionalChange,
  selectedProfessionalId,
  selectedDate,
  renderDayContent,
}: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarMonth, setCalendarMonth] = useState<CalendarMonth | null>(null);
  const [dayPopupOpen, setDayPopupOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);

  // Generate calendar data when month changes or slots are updated
  useEffect(() => {
    if (!slots) return;

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDayOfMonth = startOfMonth(new Date(year, month));
    const lastDayOfMonth = endOfMonth(new Date(year, month));

    // Get all days in the month
    const daysInMonth = eachDayOfInterval({
      start: firstDayOfMonth,
      end: lastDayOfMonth,
    });

    // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
    const startingDayIndex = getDay(firstDayOfMonth);

    // Create calendar days array with proper padding
    const calendarDays: CalendarDay[] = [];

    // Add days from previous month to fill the first week
    const daysFromPreviousMonth = startingDayIndex;
    for (let i = daysFromPreviousMonth - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      const dateString = format(date, 'yyyy-MM-dd');

      // Find slots for this day
      const daySlots = slots.filter(slot => {
        // Ensure we're comparing just the date part (YYYY-MM-DD)
        const slotDate = slot.date.split('T')[0];
        const compareDate = dateString;
        return slotDate === compareDate;
      });

      calendarDays.push({
        date: dateString,
        dayOfMonth: date.getDate(),
        isCurrentMonth: false,
        isToday: isToday(date),
        hasSlots: daySlots.length > 0,
        slots: daySlots,
      });
    }

    // Add days of the current month
    daysInMonth.forEach(date => {
      const dateString = format(date, 'yyyy-MM-dd');

      // Find slots for this day
      const daySlots = slots.filter(slot => {
        // Ensure we're comparing just the date part (YYYY-MM-DD)
        const slotDate = slot.date.split('T')[0];
        const compareDate = dateString;
        return slotDate === compareDate;
      });

      calendarDays.push({
        date: dateString,
        dayOfMonth: date.getDate(),
        isCurrentMonth: true,
        isToday: isToday(date),
        hasSlots: daySlots.length > 0,
        slots: daySlots,
      });
    });

    // Add days from next month to complete the grid (to make it 6 rows x 7 columns = 42 cells)
    const totalDaysToShow = 42;
    const remainingDays = totalDaysToShow - calendarDays.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      const dateString = format(date, 'yyyy-MM-dd');

      // Find slots for this day
      const daySlots = slots.filter(slot => {
        // Ensure we're comparing just the date part (YYYY-MM-DD)
        const slotDate = slot.date.split('T')[0];
        const compareDate = dateString;
        return slotDate === compareDate;
      });

      calendarDays.push({
        date: dateString,
        dayOfMonth: date.getDate(),
        isCurrentMonth: false,
        isToday: isToday(date),
        hasSlots: daySlots.length > 0,
        slots: daySlots,
      });
    }

    setCalendarMonth({
      year,
      month,
      days: calendarDays,
    });
  }, [currentDate, slots]);

  const goToPreviousMonth = () => {
    setCurrentDate(prevDate => subMonths(prevDate, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prevDate => addMonths(prevDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleProfessionalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onProfessionalChange(value ? parseInt(value, 10) : 0);
  };

  const handleDayClick = (day: CalendarDay) => {
    setSelectedDay(day);
    setDayPopupOpen(true);
    onDateClick(day.date);
  };

  const renderDaySlotSummary = (day: CalendarDay) => {
    if (!day.hasSlots) return null;

    // Group slots by status
    const availableCount = day.slots.filter(s => s.status === 'available').length;
    const bookedCount = day.slots.filter(s => s.status === 'booked').length;
    const confirmedCount = day.slots.filter(s => s.status === 'confirmed').length;

    return (
      <div className="mt-1 flex flex-wrap gap-1">
        {availableCount > 0 && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {availableCount}
          </span>
        )}
        {bookedCount > 0 && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            {bookedCount}
          </span>
        )}
        {confirmedCount > 0 && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {confirmedCount}
          </span>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={goToPreviousMonth}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h2 className="text-xl font-semibold min-w-[200px] text-center">
              {format(currentDate, 'MMMM yyyy', { locale: ptBR })}
            </h2>
            <Button variant="outline" onClick={goToNextMonth}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <Button variant="secondary" onClick={goToToday}>
              Hoje
            </Button>
          </div>

          <div className="flex items-center space-x-2 w-full sm:w-auto">
            <div className="w-full sm:w-64">
              <FormSelect
                value={selectedProfessionalId?.toString() || ''}
                onChange={handleProfessionalChange}
              >
                <option value="">Todos os profissionais</option>
                {professionals.map(professional => (
                  <option key={professional.id} value={professional.id}>
                    {professional.name}
                  </option>
                ))}
              </FormSelect>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="grid grid-cols-7 gap-px border-b">
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day, i) => (
              <div
                key={i}
                className="bg-gray-50 py-2 text-center text-sm font-semibold text-gray-700"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-px bg-gray-200">
            {calendarMonth?.days.map((day, i) => {
              const isSelected = selectedDate === day.date;

              return (
                <div
                  key={i}
                  className={cn(
                    'bg-white p-2 h-24 sm:h-32 md:h-40 overflow-hidden',
                    !day.isCurrentMonth && 'bg-gray-50 text-gray-400',
                    day.isToday && 'bg-blue-50',
                    'hover:bg-gray-50 cursor-pointer transition-colors'
                  )}
                  onClick={() => handleDayClick(day)}
                >
                  <div className="flex justify-between">
                    <span
                      className={cn(
                        'text-sm font-medium',
                        day.isToday && 'text-blue-600 font-bold',
                        isSelected && 'text-primary-600'
                      )}
                    >
                      {day.dayOfMonth}
                    </span>
                    {day.hasSlots && (
                      <span className="text-xs text-gray-500">{day.slots.length} slots</span>
                    )}
                  </div>
                  {renderDaySlotSummary(day)}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Day Popup Modal */}
      <Modal
        isOpen={dayPopupOpen}
        onClose={() => setDayPopupOpen(false)}
        title={
          selectedDay
            ? format(parseISO(selectedDay.date), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })
            : 'Detalhes do Dia'
        }
        size="xl"
      >
        {selectedDay && renderDayContent(selectedDay.date)}
      </Modal>
    </>
  );
}

export default CalendarView;
